import * as bitcoin from 'bitcoinjs-lib';
import { nip19 } from 'nostr-tools';
import { secp256k1, schnorr } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha256';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Base URL for the mempool.space Esplora-compatible REST API. */
const MEMPOOL_API = 'https://mempool.space/api';

/** Standard Bitcoin dust limit in satoshis. */
const DUST_LIMIT = 546;

/** Estimated vBytes per P2TR input (key-path spend). */
const VBYTES_PER_INPUT = 57.5;

/** Estimated vBytes for transaction overhead (version, locktime, segwit marker). */
const VBYTES_OVERHEAD = 10.5;

// Per-output vbyte costs by script type
/** P2TR output: OP_1 <32-byte key> = 34 bytes scriptPubKey + 8 value + 1 len = 43 vB */
const VBYTES_OUT_P2TR = 43;
/** P2WPKH output: OP_0 <20-byte hash> = 22 bytes + 8 + 1 = 31 vB */
const VBYTES_OUT_P2WPKH = 31;
/** P2WSH output: OP_0 <32-byte hash> = 34 bytes + 8 + 1 = 43 vB */
const VBYTES_OUT_P2WSH = 43;
/** P2PKH output: OP_DUP OP_HASH160 <20> OP_EQUALVERIFY OP_CHECKSIG = 25 bytes + 8 + 1 = 34 vB */
const VBYTES_OUT_P2PKH = 34;
/** P2SH output: OP_HASH160 <20> OP_EQUAL = 23 bytes + 8 + 1 = 32 vB */
const VBYTES_OUT_P2SH = 32;

/** Default output vbytes when type is unknown — use the largest (P2TR) to be safe. */
const VBYTES_PER_OUTPUT = VBYTES_OUT_P2TR;

// ---------------------------------------------------------------------------
// SHA256 helpers
// ---------------------------------------------------------------------------

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Bitcoin-style tagged hash (BIP-340).
 * tagHash = SHA256(tag), then SHA256(tagHash || tagHash || data)
 */
function taggedHash(tag: string, data: Uint8Array): Uint8Array {
  const tagBytes = new TextEncoder().encode(tag);
  const tagHash = sha256(tagBytes);
  const toHash = new Uint8Array(tagHash.length * 2 + data.length);
  toHash.set(tagHash, 0);
  toHash.set(tagHash, tagHash.length);
  toHash.set(data, tagHash.length * 2);
  return sha256(toHash);
}

/** Extract the 32-byte x-only pubkey from a 33-byte compressed pubkey. */
function toXOnly(pubkey: Uint8Array): Uint8Array {
  return pubkey.slice(1, 33);
}

// ---------------------------------------------------------------------------
// ECC library adapter for bitcoinjs-lib v6
// ---------------------------------------------------------------------------
//
// bitcoinjs-lib v6's `initEccLib` validates by checking:
//  - isXOnlyPoint(Buffer32): boolean
//  - xOnlyPointAddTweak(p: Buffer32, tweak: Buffer32): { parity: 0|1, xOnlyPubkey: Uint8Array } | null
//
// Additional methods used internally by bitcoinjs-lib:
//  - isPrivate, pointFromScalar, sign, signSchnorr, verify, verifySchnorr, etc.

const eccLib = {
  isXOnlyPoint(p: Uint8Array): boolean {
    if (p.length !== 32) return false;
    if (p.every((b) => b === 0)) return false;
    // Field prime p = 2^256 - 2^32 - 977
    const FIELD_PRIME = BigInt('0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFC2F');
    const x = BigInt('0x' + bytesToHex(p));
    if (x >= FIELD_PRIME) return false;
    try {
      // Try to lift x to a curve point
      secp256k1.ProjectivePoint.fromHex(bytesToHex(new Uint8Array([0x02, ...p])));
      return true;
    } catch {
      try {
        secp256k1.ProjectivePoint.fromHex(bytesToHex(new Uint8Array([0x03, ...p])));
        return true;
      } catch {
        return false;
      }
    }
  },

  xOnlyPointAddTweak(
    p: Uint8Array,
    tweak: Uint8Array,
  ): { parity: 0 | 1; xOnlyPubkey: Uint8Array } | null {
    try {
      const ORDER = secp256k1.CURVE.n as bigint;
      const tw = BigInt('0x' + bytesToHex(tweak));
      if (tw >= ORDER) return null;

      // Try both parities to find the actual point
      let point: ReturnType<typeof secp256k1.ProjectivePoint.fromHex> | null = null;
      try {
        point = secp256k1.ProjectivePoint.fromHex(bytesToHex(new Uint8Array([0x02, ...p])));
      } catch {
        try {
          point = secp256k1.ProjectivePoint.fromHex(bytesToHex(new Uint8Array([0x03, ...p])));
        } catch {
          return null;
        }
      }

      const tweakPoint = secp256k1.ProjectivePoint.BASE.multiply(tw);
      const result = point.add(tweakPoint);
      const resultBytes = result.toRawBytes(true); // compressed, 33 bytes
      const parity = resultBytes[0] === 0x02 ? 0 : 1;
      const xOnlyPubkey = resultBytes.slice(1, 33);

      return { parity: parity as 0 | 1, xOnlyPubkey };
    } catch {
      return null;
    }
  },

  isPrivate(d: Uint8Array): boolean {
    try {
      secp256k1.getPublicKey(d, true);
      return true;
    } catch {
      return false;
    }
  },

  pointFromScalar(privKey: Uint8Array, compressed = true): Uint8Array | null {
    try {
      return secp256k1.getPublicKey(privKey, compressed);
    } catch {
      return null;
    }
  },

  pointCompress(point: Uint8Array, compressed = true): Uint8Array {
    return secp256k1.ProjectivePoint.fromHex(bytesToHex(point)).toRawBytes(compressed);
  },

  pointMultiply(point: Uint8Array, scalar: Uint8Array, compressed = true): Uint8Array | null {
    try {
      const p = secp256k1.ProjectivePoint.fromHex(bytesToHex(point)).multiply(BigInt('0x' + bytesToHex(scalar)));
      return p.toRawBytes(compressed);
    } catch {
      return null;
    }
  },

  pointAdd(pA: Uint8Array, pB: Uint8Array, compressed = true): Uint8Array | null {
    try {
      const a = secp256k1.ProjectivePoint.fromHex(bytesToHex(pA));
      const b = secp256k1.ProjectivePoint.fromHex(bytesToHex(pB));
      return a.add(b).toRawBytes(compressed);
    } catch {
      return null;
    }
  },

  pointAddScalar(point: Uint8Array, scalar: Uint8Array, compressed = true): Uint8Array | null {
    try {
      const p = secp256k1.ProjectivePoint.fromHex(bytesToHex(point));
      const s = BigInt('0x' + bytesToHex(scalar));
      const q = secp256k1.ProjectivePoint.BASE.multiply(s);
      return p.add(q).toRawBytes(compressed);
    } catch {
      return null;
    }
  },

  privateAdd(privKey: Uint8Array, tweak: Uint8Array): Uint8Array | null {
    try {
      const n = secp256k1.CURVE.n as bigint;
      const sk = BigInt('0x' + bytesToHex(privKey));
      const tw = BigInt('0x' + bytesToHex(tweak));
      const result = (sk + tw) % n;
      if (result === BigInt(0)) return null;
      return hexToBytes(result.toString(16).padStart(64, '0'));
    } catch {
      return null;
    }
  },

  sign(hash: Uint8Array, privKey: Uint8Array, lowR?: boolean): Uint8Array {
    const sig = secp256k1.sign(hash, privKey, { lowS: lowR ?? false });
    return sig.toDERRawBytes();
  },

  signSchnorr(hash: Uint8Array, privKey: Uint8Array): Uint8Array {
    return schnorr.sign(hash, privKey) as Uint8Array;
  },

  verify(hash: Uint8Array, pubKey: Uint8Array, sig: Uint8Array, strict?: boolean): boolean {
    try {
      const signature = secp256k1.Signature.fromDER(sig);
      return secp256k1.verify(signature, hash, pubKey, { lowS: strict });
    } catch {
      return false;
    }
  },

  verifySchnorr(hash: Uint8Array, pubKey: Uint8Array, sig: Uint8Array): boolean {
    try {
      return schnorr.verify(sig, hash, pubKey) as boolean;
    } catch {
      return false;
    }
  },
};

let _eccInitialized = false;

/** Call before any operation that requires ECC (address derivation, signing, etc.). */
export function ensureEccInitialized(): void {
  if (_eccInitialized) return;
  bitcoin.initEccLib(eccLib as Parameters<typeof bitcoin.initEccLib>[0]);
  _eccInitialized = true;
}

// ---------------------------------------------------------------------------
// Strict 32-byte hex validator
// ---------------------------------------------------------------------------

function isValidPubkeyHex(hex: string): boolean {
  return typeof hex === 'string' && /^[0-9a-fA-F]{64}$/.test(hex);
}

/**
 * Convert a Nostr public key (32-byte hex) to a Bitcoin Taproot (P2TR) address.
 */
export function nostrPubkeyToBitcoinAddress(pubkeyHex: string): string {
  if (!isValidPubkeyHex(pubkeyHex)) return '';

  try {
    ensureEccInitialized();
    const pubkeyBuffer = Buffer.from(pubkeyHex, 'hex');

    const { address } = bitcoin.payments.p2tr({
      internalPubkey: pubkeyBuffer,
      network: bitcoin.networks.bitcoin,
    });

    return address || '';
  } catch (error) {
    console.error('Error generating Bitcoin address:', error);
    return '';
  }
}

/**
 * Convert a bech32 `npub1...` identifier to a Bitcoin Taproot (P2TR) address.
 */
export function npubToBitcoinAddress(npub: string): string {
  const decoded = nip19.decode(npub);
  if (decoded.type !== 'npub') {
    throw new Error('Invalid npub format');
  }
  return nostrPubkeyToBitcoinAddress(decoded.data);
}

// ---------------------------------------------------------------------------
// Balance / Address data (wallet page)
// ---------------------------------------------------------------------------

export interface AddressData {
  balance: number;
  pendingBalance: number;
  totalBalance: number;
  totalReceived: number;
  totalSent: number;
  txCount: number;
  pendingTxCount: number;
}

export async function fetchAddressData(address: string): Promise<AddressData> {
  const response = await fetch(`${MEMPOOL_API}/address/${address}`);

  if (!response.ok) {
    throw new Error('Failed to fetch balance');
  }

  const data = await response.json();

  const confirmedBalance = data.chain_stats.funded_txo_sum - data.chain_stats.spent_txo_sum;
  const pendingBalance = data.mempool_stats.funded_txo_sum - data.mempool_stats.spent_txo_sum;

  return {
    balance: confirmedBalance,
    pendingBalance,
    totalBalance: confirmedBalance + pendingBalance,
    totalReceived: data.chain_stats.funded_txo_sum,
    totalSent: data.chain_stats.spent_txo_sum,
    txCount: data.chain_stats.tx_count,
    pendingTxCount: data.mempool_stats.tx_count,
  };
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export function satsToBTC(sats: number): string {
  return (sats / 100_000_000).toFixed(8);
}

export function formatBTC(sats: number): string {
  return satsToBTC(sats).replace(/\.?0+$/, '');
}

export function formatSats(sats: number): string {
  return sats.toLocaleString();
}

export async function fetchBtcPrice(): Promise<number> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd',
  );

  if (!response.ok) {
    throw new Error('Failed to fetch BTC price');
  }

  const data = await response.json();
  return data.bitcoin.usd;
}

export function btcToSats(btc: number): number {
  return Math.round(btc * 100_000_000);
}

export const LARGE_AMOUNT_USD_THRESHOLD = 100;

export function isLargeAmount(sats: number, btcPrice: number | undefined): boolean {
  if (!btcPrice || !Number.isFinite(btcPrice) || btcPrice <= 0) return false;
  if (!Number.isFinite(sats) || sats <= 0) return false;
  const usd = (sats / 100_000_000) * btcPrice;
  return usd >= LARGE_AMOUNT_USD_THRESHOLD;
}

export function satsToUSD(sats: number, btcPrice: number): string {
  const btc = sats / 100_000_000;
  return (btc * btcPrice).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ---------------------------------------------------------------------------
// Transaction list
// ---------------------------------------------------------------------------

export interface Transaction {
  txid: string;
  amount: number;
  type: 'receive' | 'send';
  confirmed: boolean;
  timestamp?: number;
}

export async function fetchTransactions(address: string): Promise<Transaction[]> {
  const response = await fetch(`${MEMPOOL_API}/address/${address}/txs`);

  if (!response.ok) {
    throw new Error('Failed to fetch transactions');
  }

  const txs = await response.json();

  return txs.map((tx: Record<string, unknown>) => {
    const vin = tx.vin as Array<{ prevout: { scriptpubkey_address?: string; value: number } | null }>;
    const vout = tx.vout as Array<{ scriptpubkey_address?: string; value: number }>;
    const status = tx.status as { confirmed: boolean; block_time?: number };

    const totalIn = vin.reduce((sum, input) => {
      if (input.prevout?.scriptpubkey_address === address) return sum + input.prevout.value;
      return sum;
    }, 0);

    const totalOut = vout.reduce((sum, output) => {
      if (output.scriptpubkey_address === address) return sum + output.value;
      return sum;
    }, 0);

    const net = totalOut - totalIn;

    return {
      txid: tx.txid as string,
      amount: Math.abs(net),
      type: net >= 0 ? 'receive' : 'send',
      confirmed: status.confirmed,
      timestamp: status.block_time,
    } satisfies Transaction;
  });
}

// ---------------------------------------------------------------------------
// Full transaction detail
// ---------------------------------------------------------------------------

export interface TxInput {
  txid: string;
  vout: number;
  address?: string;
  value: number;
  isCoinbase: boolean;
}

export interface TxOutput {
  address?: string;
  value: number;
  scriptpubkeyType: string;
  spent: boolean;
}

export interface TxDetail {
  txid: string;
  version: number;
  locktime: number;
  size: number;
  weight: number;
  fee: number;
  confirmed: boolean;
  blockHeight?: number;
  blockHash?: string;
  blockTime?: number;
  inputs: TxInput[];
  outputs: TxOutput[];
  totalInput: number;
  totalOutput: number;
}

export async function fetchTxDetail(txid: string): Promise<TxDetail> {
  const response = await fetch(`${MEMPOOL_API}/tx/${txid}`);
  if (!response.ok) throw new Error('Failed to fetch transaction');

  const tx = await response.json();

  const vin = tx.vin as Array<{
    txid: string;
    vout: number;
    prevout: { scriptpubkey_address?: string; value: number } | null;
    is_coinbase: boolean;
  }>;
  const vout = tx.vout as Array<{
    scriptpubkey_address?: string;
    value: number;
    scriptpubkey_type: string;
  }>;
  const status = tx.status as { confirmed: boolean; block_height?: number; block_hash?: string; block_time?: number };

  const inputs: TxInput[] = vin.map((input) => ({
    txid: input.txid,
    vout: input.vout,
    address: input.prevout?.scriptpubkey_address,
    value: input.prevout?.value ?? 0,
    isCoinbase: input.is_coinbase,
  }));

  const outputs: TxOutput[] = vout.map((output) => ({
    address: output.scriptpubkey_address,
    value: output.value,
    scriptpubkeyType: output.scriptpubkey_type,
    spent: false,
  }));

  return {
    txid: tx.txid as string,
    version: tx.version as number,
    locktime: tx.locktime as number,
    size: tx.size as number,
    weight: tx.weight as number,
    fee: tx.fee as number,
    confirmed: status.confirmed,
    blockHeight: status.block_height,
    blockHash: status.block_hash,
    blockTime: status.block_time,
    inputs,
    outputs,
    totalInput: inputs.reduce((sum, i) => sum + i.value, 0),
    totalOutput: outputs.reduce((sum, o) => sum + o.value, 0),
  };
}

// ---------------------------------------------------------------------------
// Full address detail
// ---------------------------------------------------------------------------

export interface AddressDetail {
  address: string;
  balance: number;
  pendingBalance: number;
  totalBalance: number;
  totalReceived: number;
  totalSent: number;
  txCount: number;
  pendingTxCount: number;
  recentTxs: Transaction[];
}

export async function fetchAddressDetail(address: string): Promise<AddressDetail> {
  const [addrData, txs] = await Promise.all([fetchAddressData(address), fetchTransactions(address)]);
  return { address, ...addrData, recentTxs: txs.slice(0, 25) };
}

// ---------------------------------------------------------------------------
// Sending: UTXOs, fee estimation, PSBT construction, broadcast
// ---------------------------------------------------------------------------

export interface UTXO {
  txid: string;
  vout: number;
  value: number;
  status: {
    confirmed: boolean;
    block_height?: number;
    block_hash?: string;
    block_time?: number;
  };
}

export async function fetchUTXOs(address: string): Promise<UTXO[]> {
  const response = await fetch(`${MEMPOOL_API}/address/${address}/utxo`);
  if (!response.ok) throw new Error('Failed to fetch UTXOs');
  const all: UTXO[] = await response.json();
  // Only spend confirmed UTXOs — unconfirmed outputs may be double-spent or RBF'd.
  return all.filter((u) => u.status.confirmed);
}

export interface FeeRates {
  fastestFee: number;
  halfHourFee: number;
  hourFee: number;
  economyFee: number;
  minimumFee: number;
}

export async function getFeeRates(): Promise<FeeRates> {
  const response = await fetch(`${MEMPOOL_API}/fee-estimates`);
  if (!response.ok) throw new Error('Failed to fetch fee estimates');

  const data = await response.json();

  // Keep fractional sat/vB rates — mempool.space regularly returns sub-1 values
  // (e.g. 0.68 sat/vB) during low-congestion periods and those are valid/accepted.
  // Just guard against zero or negative values from malformed responses.
  const parse = (v: unknown) => Math.max(0.001, Number(v) || 0.001);

  return {
    fastestFee:  parse(data['1']),
    halfHourFee: parse(data['3']),
    hourFee:     parse(data['6']),
    economyFee:  parse(data['144']),
    minimumFee:  parse(data['504']),
  };
}

/**
 * Estimate the fee for a P2TR-input transaction in satoshis.
 *
 * @param numInputs       Number of Taproot inputs (key-path spend).
 * @param numOutputs      Number of outputs.
 * @param feeRate         Fee rate in sat/vB.
 * @param recipientVbytes vBytes for the recipient output (defaults to P2TR).
 *                        Change output always goes back to a P2TR address.
 */
export function estimateFee(
  numInputs: number,
  numOutputs: number,
  feeRate: number,
  recipientVbytes = VBYTES_PER_OUTPUT,
): number {
  // First output = recipient (variable type), remaining = change (P2TR)
  const outputVbytes =
    recipientVbytes + (numOutputs > 1 ? (numOutputs - 1) * VBYTES_OUT_P2TR : 0);
  const vBytes = numInputs * VBYTES_PER_INPUT + outputVbytes + VBYTES_OVERHEAD;
  return Math.ceil(vBytes * feeRate);
}

export function validateBitcoinAddress(address: string): boolean {
  try {
    ensureEccInitialized();
    bitcoin.address.toOutputScript(address, bitcoin.networks.bitcoin);
    return true;
  } catch {
    return false;
  }
}

/** Address format categories shown to the user. */
export type AddressType = 'p2tr' | 'p2wpkh' | 'p2wsh' | 'p2pkh' | 'p2sh' | 'unknown';

/**
 * Classify a mainnet Bitcoin address into its script type.
 * Returns 'unknown' for invalid or unrecognised addresses.
 */
export function getAddressType(address: string): AddressType {
  if (!address) return 'unknown';
  // Native SegWit v1 (Taproot): bc1p…
  if (address.startsWith('bc1p')) return 'p2tr';
  // Native SegWit v0 20-byte (P2WPKH): bc1q… (39 chars)
  if (address.startsWith('bc1q') && address.length === 42) return 'p2wpkh';
  // Native SegWit v0 32-byte (P2WSH): bc1q… (62 chars)
  if (address.startsWith('bc1q') && address.length === 62) return 'p2wsh';
  // Legacy P2PKH: starts with 1
  if (address.startsWith('1')) return 'p2pkh';
  // Legacy P2SH: starts with 3
  if (address.startsWith('3')) return 'p2sh';
  return 'unknown';
}

/** Human-readable label for each address type. */
export function addressTypeLabel(type: AddressType): string {
  switch (type) {
    case 'p2tr':    return 'Taproot (P2TR)';
    case 'p2wpkh':  return 'Native SegWit (P2WPKH)';
    case 'p2wsh':   return 'Native SegWit (P2WSH)';
    case 'p2pkh':   return 'Legacy (P2PKH)';
    case 'p2sh':    return 'Script Hash (P2SH)';
    default:        return 'Unknown';
  }
}

/** Output vbyte cost for a given address type. */
export function outputVbytesForType(type: AddressType): number {
  switch (type) {
    case 'p2tr':   return VBYTES_OUT_P2TR;
    case 'p2wpkh': return VBYTES_OUT_P2WPKH;
    case 'p2wsh':  return VBYTES_OUT_P2WSH;
    case 'p2pkh':  return VBYTES_OUT_P2PKH;
    case 'p2sh':   return VBYTES_OUT_P2SH;
    default:       return VBYTES_PER_OUTPUT;
  }
}

/**
 * Parse a mempool.space / Bitcoin Core broadcast error body into a
 * human-readable string.
 *
 * mempool.space relays the raw bitcoind JSON-RPC error when a transaction is
 * rejected. The body looks like:
 *   sendrawtransaction RPC error: {"code":-22,"message":"TX decode failed: …"}
 * or just a plain string like:
 *   bad-txns-inputs-missingorspent
 *
 * We extract the innermost `message` field so the user sees something useful.
 */
function parseBroadcastError(body: string): string {
  // Try to extract the JSON payload embedded in the RPC error string
  const jsonMatch = body.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]) as { message?: string; error?: string };
      const inner = parsed.message ?? parsed.error;
      if (inner) {
        // Strip noisy prefixes like "TX decode failed: "
        return inner.replace(/^TX decode failed:\s*/i, '').trim();
      }
    } catch {
      // fall through to plain text
    }
  }
  // Return the raw body stripped of the "sendrawtransaction RPC error: " prefix
  return body.replace(/^sendrawtransaction RPC error:\s*/i, '').trim() || body;
}

export async function broadcastTransaction(txHex: string): Promise<string> {
  const response = await fetch(`${MEMPOOL_API}/tx`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: txHex,
  });

  if (!response.ok) {
    const body = await response.text();
    const parsed = parseBroadcastError(body);
    throw new BroadcastError(parsed, body);
  }

  return response.text();
}

/**
 * Thrown when mempool.space rejects a broadcast. Carries both the
 * human-readable `message` (for display) and the raw `body` (for logging).
 */
export class BroadcastError extends Error {
  readonly rawBody: string;
  constructor(message: string, rawBody: string) {
    super(message);
    this.name = 'BroadcastError';
    this.rawBody = rawBody;
  }
}

export function maxSendable(
  totalBalance: number,
  numInputs: number,
  feeRate: number,
  recipientVbytes = VBYTES_PER_OUTPUT,
): number {
  const fee = estimateFee(numInputs, 1, feeRate, recipientVbytes);
  return Math.max(0, totalBalance - fee);
}

// ---------------------------------------------------------------------------
// PSBT building without `new bitcoin.Psbt()`
//
// bitcoinjs-lib's Psbt constructor evaluates a default parameter that calls
// Transaction.fromBuffer on a hardcoded buffer, which throws
// "Transaction has unexpected data" in this browser bundle.
//
// Fix: build the unsigned transaction with bitcoin.Transaction (which is
// fine) then serialise a proper BIP-174 PSBT binary manually so that NIP-07
// extensions and NIP-46 remote signers receive a real, standard PSBT hex
// (as required by NIP-BC §Signer Methods). For local nsec signing we also
// compute the sighashes directly from Transaction.hashForWitnessV1 — again
// never touching the Psbt constructor.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Minimal BIP-174 PSBT serialiser
//
// BIP-174 PSBT binary layout:
//   magic: 0x70736274 ff
//   global map: key-value pairs terminated by 0x00
//     key 0x00: unsigned tx (length-prefixed)
//   per-input maps (one per tx input), each terminated by 0x00
//     key 0x01: witnessUtxo  (value: 8-byte LE amount || varint len || script)
//     key 0xfc 0x00 0x00 0x14 (tapInternalKey): 32-byte x-only pubkey
//   per-output maps (one per tx output), each 0x00 (empty)
// ---------------------------------------------------------------------------

function psbtVarInt(n: number): Uint8Array {
  if (n < 0xfd) return new Uint8Array([n]);
  if (n <= 0xffff) return new Uint8Array([0xfd, n & 0xff, (n >> 8) & 0xff]);
  // We won't have >65535-byte fields here
  throw new Error('varint overflow');
}

function psbtKeyValue(key: Uint8Array, value: Uint8Array): Uint8Array {
  const kLen = psbtVarInt(key.length);
  const vLen = psbtVarInt(value.length);
  const out = new Uint8Array(kLen.length + key.length + vLen.length + value.length);
  let off = 0;
  out.set(kLen, off); off += kLen.length;
  out.set(key, off); off += key.length;
  out.set(vLen, off); off += vLen.length;
  out.set(value, off);
  return out;
}

function uint64LE(n: number): Uint8Array {
  const buf = new Uint8Array(8);
  const lo = n >>> 0;
  const hi = Math.floor(n / 0x100000000) >>> 0;
  buf[0] = lo & 0xff; buf[1] = (lo >> 8) & 0xff;
  buf[2] = (lo >> 16) & 0xff; buf[3] = (lo >> 24) & 0xff;
  buf[4] = hi & 0xff; buf[5] = (hi >> 8) & 0xff;
  buf[6] = (hi >> 16) & 0xff; buf[7] = (hi >> 24) & 0xff;
  return buf;
}

function concat(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}

/**
 * Build a BIP-174 PSBT binary for a transaction spending P2TR key-path inputs.
 * Returns the PSBT as a hex string suitable for NIP-07 / NIP-46 `signPsbt`.
 *
 * Does NOT use `new bitcoin.Psbt()` — avoids the broken default-parameter
 * path in the browser bundle.
 */
function buildPsbtHex(
  tx: bitcoin.Transaction,
  inputScripts: Buffer[],
  inputValues: number[],
  internalPubkey: Buffer,  // 32-byte x-only
): string {
  const parts: Uint8Array[] = [];

  // Magic + separator
  parts.push(new Uint8Array([0x70, 0x73, 0x62, 0x74, 0xff]));

  // Global: unsigned tx (key type 0x00)
  const txBytes = tx.toBuffer();
  parts.push(psbtKeyValue(new Uint8Array([0x00]), txBytes));
  parts.push(new Uint8Array([0x00])); // end of global map

  // Per-input maps
  for (let i = 0; i < tx.ins.length; i++) {
    const script = inputScripts[i];
    const value = inputValues[i];

    // witnessUtxo: key type 0x01
    // value = 8-byte LE value || varint-len || scriptPubKey
    const scriptLen = psbtVarInt(script.length);
    const witnessUtxoValue = concat(uint64LE(value), scriptLen, script);
    parts.push(psbtKeyValue(new Uint8Array([0x01]), witnessUtxoValue));

    // tapInternalKey: key type 0xfc 0x00 0x00 0x14 (proprietary? no — BIP-371 type 0x14)
    // BIP-371: PSBT_IN_TAP_INTERNAL_KEY = 0x14
    parts.push(psbtKeyValue(new Uint8Array([0x14]), internalPubkey));

    parts.push(new Uint8Array([0x00])); // end of input map
  }

  // Per-output maps (empty — no taproot output keys needed for signing)
  for (let i = 0; i < tx.outs.length; i++) {
    parts.push(new Uint8Array([0x00]));
  }

  return bytesToHex(concat(...parts));
}

/**
 * Structured signing data for the local nsec path.
 * Passed as base64 JSON so it fits the `signPsbt(string): string` interface
 * without ever going through bitcoinjs Transaction.toBuffer / fromBuffer.
 */
export interface LocalSigningToken {
  version: number;
  locktime: number;
  inputs: Array<{ hashHex: string; index: number; sequence: number }>;
  outputs: Array<{ value: number; scriptHex: string }>;
  prevoutScripts: string[];  // hex scriptPubKey per input
  prevoutValues: number[];   // satoshi value per input
}

export interface UnsignedPsbt {
  psbtHex: string;         // Real BIP-174 PSBT hex for NIP-07/NIP-46 signers
  localToken: string;      // base64(JSON(LocalSigningToken)) for nsec local signing
  inputScripts: string[];  // Hex prevout scriptPubKey per input (convenience)
  inputValues: number[];   // Satoshi value per input (convenience)
  fee: number;
}

export function buildUnsignedPsbt(
  senderPubkeyHex: string,
  toAddress: string,
  amountSats: number,
  utxos: UTXO[],
  feeRate: number,
): UnsignedPsbt {
  ensureEccInitialized();
  const internalPubkey = Buffer.from(senderPubkeyHex, 'hex'); // 32-byte x-only

  // Derive P2TR payment for the sender (inputs + change).
  const senderP2tr = bitcoin.payments.p2tr({
    internalPubkey,
    network: bitcoin.networks.bitcoin,
  });
  if (!senderP2tr.address || !senderP2tr.output) throw new Error('Failed to derive sender P2TR address');
  const inputScript = senderP2tr.output;

  // Pre-validate recipient address and get output script.
  const recipientScript = bitcoin.address.toOutputScript(toAddress, bitcoin.networks.bitcoin);
  const changeScript = inputScript; // change → sender's own P2TR

  const recipientVbytes = outputVbytesForType(getAddressType(toAddress));

  let totalInput = 0;
  for (const utxo of utxos) totalInput += utxo.value;

  const change2Out = totalInput - amountSats - estimateFee(utxos.length, 2, feeRate, recipientVbytes);
  const hasChange = change2Out >= DUST_LIMIT;
  const numOutputs = hasChange ? 2 : 1;
  const fee = estimateFee(utxos.length, numOutputs, feeRate, recipientVbytes);
  const change = totalInput - amountSats - fee;

  if (change < 0) {
    throw new Error(
      `Insufficient funds. Need ${(amountSats + fee).toLocaleString()} sats, have ${totalInput.toLocaleString()} sats.`,
    );
  }

  // Build the unsigned transaction directly — no Psbt constructor.
  const tx = new bitcoin.Transaction();
  tx.version = 2;
  for (const utxo of utxos) {
    tx.addInput(Buffer.from(utxo.txid, 'hex').reverse() as Buffer, utxo.vout, 0xffffffff);
  }
  tx.addOutput(recipientScript, amountSats);
  if (hasChange) tx.addOutput(changeScript, change);

  const inputScripts = utxos.map(() => bytesToHex(inputScript));
  const inputValues  = utxos.map((u) => u.value);

  // Build a proper BIP-174 PSBT for NIP-07 / NIP-46 signers.
  const psbtHex = buildPsbtHex(
    tx,
    utxos.map(() => inputScript),
    inputValues,
    internalPubkey,
  );

  // Build a local signing token for the nsec path.
  // Extracted directly from the in-memory Transaction — never uses toBuffer/toHex,
  // avoiding the broken bitcoinjs Transaction serialization in the browser bundle.
  const localToken: LocalSigningToken = {
    version: tx.version,
    locktime: tx.locktime,
    inputs: tx.ins.map((inp) => ({
      hashHex: bytesToHex(inp.hash),   // already stored reversed (little-endian)
      index: inp.index,
      sequence: inp.sequence,
    })),
    outputs: tx.outs.map((out) => ({
      value: out.value,
      scriptHex: bytesToHex(out.script),
    })),
    prevoutScripts: inputScripts,
    prevoutValues: inputValues,
  };

  return {
    psbtHex,
    localToken: btoa(JSON.stringify(localToken)),
    inputScripts,
    inputValues,
    fee,
  };
}

// ---------------------------------------------------------------------------
// BIP-341 sighash (SIGHASH_DEFAULT / key-path spend) — pure @noble impl
//
// The bundled bitcoinjs hashForWitnessV1 uses internal typeforce Buffer checks
// that silently corrupt the BufferWriter offset when passed browser-polyfill
// Uint8Arrays, producing NaN offsets and wrong hashes.  We implement the
// sighash entirely with @noble/hashes sha256 + manual byte packing instead.
// ---------------------------------------------------------------------------

function writeUint32LE(n: number, out: number[]): void {
  out.push(n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >>> 24) & 0xff);
}

function writeUint64LE(n: number, out: number[]): void {
  const lo = n >>> 0;
  const hi = Math.floor(n / 0x100000000) >>> 0;
  out.push(
    lo & 0xff, (lo >> 8) & 0xff, (lo >> 16) & 0xff, (lo >>> 24) & 0xff,
    hi & 0xff, (hi >> 8) & 0xff, (hi >> 16) & 0xff, (hi >>> 24) & 0xff,
  );
}

function writeVarSlice(data: Uint8Array, out: number[]): void {
  const len = data.length;
  if (len < 0xfd) {
    out.push(len);
  } else {
    out.push(0xfd, len & 0xff, (len >> 8) & 0xff);
  }
  for (const b of data) out.push(b);
}

/**
 * BIP-341 TapSighash for SIGHASH_DEFAULT (0x00), key-path spend, no annex.
 * https://github.com/bitcoin/bips/blob/master/bip-0341.mediawiki#common-signature-message
 */
function tapSighash(
  token: LocalSigningToken,
  inputIndex: number,
  prevoutScript: Uint8Array,  // same script for all inputs (P2TR sender)
): Uint8Array {
  const nIn = token.inputs.length;

  // BIP-341 intermediate hashes all use single SHA256 (not double).
  // Reference: https://github.com/bitcoinjs/bitcoinjs-lib/blob/v6.1.5/ts_src/transaction.ts
  // "hashPrevouts = bcrypto.sha256(bufferWriter.end())"  ← single SHA256

  // hash_prevouts = SHA256( for each input: hash(32) || index(4) )
  const prevoutsBytes: number[] = [];
  for (const inp of token.inputs) {
    for (const b of hexToBytes(inp.hashHex)) prevoutsBytes.push(b);
    writeUint32LE(inp.index, prevoutsBytes);
  }
  const hashPrevouts = sha256(new Uint8Array(prevoutsBytes));

  // hash_amounts = SHA256( for each input: value(8) )
  const amountsBytes: number[] = [];
  for (const v of token.prevoutValues) writeUint64LE(v, amountsBytes);
  const hashAmounts = sha256(new Uint8Array(amountsBytes));

  // hash_scriptpubkeys = SHA256( for each input: varint||script )
  const spkBytes: number[] = [];
  for (let i = 0; i < nIn; i++) writeVarSlice(prevoutScript, spkBytes);
  const hashScriptPubKeys = sha256(new Uint8Array(spkBytes));

  // hash_sequences = SHA256( for each input: sequence(4) )
  const seqBytes: number[] = [];
  for (const inp of token.inputs) writeUint32LE(inp.sequence, seqBytes);
  const hashSequences = sha256(new Uint8Array(seqBytes));

  // hash_outputs = SHA256( for each output: value(8) || varint||script )
  const outsBytes: number[] = [];
  for (const out of token.outputs) {
    writeUint64LE(out.value, outsBytes);
    writeVarSlice(hexToBytes(out.scriptHex), outsBytes);
  }
  const hashOutputs = sha256(new Uint8Array(outsBytes));

  // sig_msg:
  //   hash_type(1)=0x00
  //   version(4)  locktime(4)
  //   hash_prevouts(32)  hash_amounts(32)  hash_scriptpubkeys(32)  hash_sequences(32)
  //   hash_outputs(32)
  //   spend_type(1)=0x00  (key-path, no annex)
  //   input_index(4)
  const msg: number[] = [];
  msg.push(0x00); // hash_type = SIGHASH_DEFAULT
  writeUint32LE(token.version, msg);
  writeUint32LE(token.locktime, msg);
  for (const b of hashPrevouts)      msg.push(b);
  for (const b of hashAmounts)       msg.push(b);
  for (const b of hashScriptPubKeys) msg.push(b);
  for (const b of hashSequences)     msg.push(b);
  for (const b of hashOutputs)       msg.push(b);
  msg.push(0x00); // spend_type (key-path, no annex)
  writeUint32LE(inputIndex, msg);

  // TapSighash = taggedHash("TapSighash", 0x00 || sig_msg)
  // BIP-341 footnote 19: prepend an extra 0x00 byte
  const payload = new Uint8Array([0x00, ...msg]);
  return taggedHash('TapSighash', payload);
}

/**
 * Serialize a signed transaction to hex (SegWit / BIP-141 format).
 * version(4) | 0x00 0x01 | inputs | outputs | witnesses | locktime(4)
 */
function serializeSignedTx(
  token: LocalSigningToken,
  witnesses: Uint8Array[][],  // per-input witness stacks
): string {
  const out: number[] = [];

  // version
  writeUint32LE(token.version, out);

  // SegWit marker + flag
  out.push(0x00, 0x01);

  // inputs
  const nIn = token.inputs.length;
  if (nIn < 0xfd) out.push(nIn); else out.push(0xfd, nIn & 0xff, (nIn >> 8) & 0xff);
  for (const inp of token.inputs) {
    for (const b of hexToBytes(inp.hashHex)) out.push(b);
    writeUint32LE(inp.index, out);
    out.push(0x00); // empty scriptSig
    writeUint32LE(inp.sequence, out);
  }

  // outputs
  const nOut = token.outputs.length;
  if (nOut < 0xfd) out.push(nOut); else out.push(0xfd, nOut & 0xff, (nOut >> 8) & 0xff);
  for (const o of token.outputs) {
    writeUint64LE(o.value, out);
    writeVarSlice(hexToBytes(o.scriptHex), out);
  }

  // witnesses (one stack per input)
  for (const stack of witnesses) {
    const items = stack.length;
    if (items < 0xfd) out.push(items); else out.push(0xfd, items & 0xff, (items >> 8) & 0xff);
    for (const item of stack) writeVarSlice(item, out);
  }

  // locktime
  writeUint32LE(token.locktime, out);

  return bytesToHex(new Uint8Array(out));
}

/**
 * Sign all P2TR inputs locally using a raw nsec private key.
 *
 * Accepts a base64-encoded `LocalSigningToken` (produced by `buildUnsignedPsbt`).
 * Uses zero bitcoinjs internals for signing or serialization — entirely
 * @noble/curves + @noble/hashes + manual byte packing to avoid the broken
 * typeforce / Buffer checks in the browser-bundled bitcoinjs.
 *
 * Returns the final broadcast-ready raw transaction hex.
 */
export function signPsbtLocal(localTokenB64: string, privateKeyHex: string): string {
  const token = JSON.parse(atob(localTokenB64)) as LocalSigningToken;

  // ── Key derivation + BIP-341 TapTweak ────────────────────────────────────
  const privKeyBytes = hexToBytes(privateKeyHex);
  const pubKey33 = secp256k1.getPublicKey(privKeyBytes, true);
  const xOnlyPubkey = pubKey33.slice(1, 33);

  const ORDER = secp256k1.CURVE.n as bigint;
  let d = BigInt('0x' + privateKeyHex);
  if (pubKey33[0] === 0x03) d = ORDER - d; // negate if odd Y

  const tweak = taggedHash('TapTweak', xOnlyPubkey);
  const tweakedPriv = hexToBytes(
    ((d + BigInt('0x' + bytesToHex(tweak))) % ORDER).toString(16).padStart(64, '0'),
  );

  // ── Derive P2TR scriptPubKey for the prevout (needed by sighash) ─────────
  // BIP-341 output key Q = P + taggedHash("TapTweak", xOnlyP)·G
  // where P is the internal key lifted to even parity (x-only).
  // We compute Q directly from the x-only internal pubkey, not from tweakedPriv,
  // because the parity-negation applied to d for signing doesn't affect Q.
  const internalPoint = secp256k1.ProjectivePoint.fromHex(
    '02' + bytesToHex(xOnlyPubkey), // lift to even-Y compressed point
  );
  const tweakScalar = BigInt('0x' + bytesToHex(tweak));
  const outputPoint = internalPoint.add(secp256k1.ProjectivePoint.BASE.multiply(tweakScalar));
  const outputXOnly = outputPoint.toRawBytes(true).slice(1, 33); // x-only
  // P2TR scriptPubKey = OP_1 (0x51) || OP_DATA_32 (0x20) || outputXOnly(32)
  const prevoutScript = new Uint8Array([0x51, 0x20, ...outputXOnly]);

  // ── Sign each input ───────────────────────────────────────────────────────
  const witnesses: Uint8Array[][] = [];
  for (let i = 0; i < token.inputs.length; i++) {
    const sighash = tapSighash(token, i, prevoutScript);
    const sig = schnorr.sign(sighash, tweakedPriv) as Uint8Array;
    witnesses.push([sig]);
  }

  // ── Serialize and return ──────────────────────────────────────────────────
  return serializeSignedTx(token, witnesses);
}

/**
 * Finalise a signed transaction.
 *
 * `signedHex` may be either:
 *   (a) a raw signed transaction hex produced by signPsbtLocal (nsec path) —
 *       signPsbtLocal now returns the final broadcast-ready tx hex directly,
 *       so this function just passes it through.
 *   (b) a signed PSBT hex returned by a NIP-07 / NIP-46 signer — we parse the
 *       tapKeySig from each input map and build the final tx using the same
 *       field-by-field approach as signPsbtLocal (no Transaction.fromBuffer).
 *
 * Returns the raw broadcast-ready transaction hex.
 */
export function finalizePsbt(signedHex: string): string {
  ensureEccInitialized();

  // Detect PSBT magic: 0x70736274ff ("psbt\xff")
  const looksLikePsbt = /^[0-9a-f]+$/i.test(signedHex) && signedHex.length >= 10 &&
    hexToBytes(signedHex.slice(0, 10)).join(',') === '112,115,98,116,255';

  if (!looksLikePsbt) {
    // nsec path: signPsbtLocal already produced the final signed tx hex.
    // Validate it looks like a real hex transaction (at minimum version+locktime).
    if (!/^[0-9a-f]+$/i.test(signedHex) || signedHex.length < 16) {
      throw new Error('finalizePsbt: unrecognised signed output from signer.');
    }
    return signedHex;
  }

  // Extension/bunker path: signed PSBT hex.
  // Parse the raw tx bytes manually (no Transaction.fromBuffer — broken in bundle),
  // collect tapKeySig per input, reconstruct Transaction, apply witnesses.
  const psbtBytes = hexToBytes(signedHex);
  let offset = 5; // skip magic

  function readVarInt(): number {
    const first = psbtBytes[offset++];
    if (first < 0xfd) return first;
    if (first === 0xfd) { const v = psbtBytes[offset] | (psbtBytes[offset + 1] << 8); offset += 2; return v; }
    throw new Error('varint too large');
  }
  function readBytes(n: number): Uint8Array {
    const s = psbtBytes.slice(offset, offset + n); offset += n; return s;
  }

  // Global map — extract raw unsigned tx bytes (key type 0x00)
  let rawTxBytes: Uint8Array | null = null;
  while (psbtBytes[offset] !== 0x00) {
    const kl = readVarInt(); const k = readBytes(kl);
    const vl = readVarInt(); const v = readBytes(vl);
    if (k[0] === 0x00) rawTxBytes = v;
  }
  offset++;
  if (!rawTxBytes) throw new Error('Signed PSBT missing unsigned transaction');

  // Parse tx fields manually — same approach as signPsbtLocal
  let txOff = 0;
  function txReadUInt32LE(): number {
    const v = rawTxBytes![txOff] | (rawTxBytes![txOff+1] << 8) |
              (rawTxBytes![txOff+2] << 16) | (rawTxBytes![txOff+3] * 0x1000000);
    txOff += 4; return v >>> 0;
  }
  function txReadVarInt(): number {
    const first = rawTxBytes![txOff++];
    if (first < 0xfd) return first;
    if (first === 0xfd) { const v = rawTxBytes![txOff] | (rawTxBytes![txOff+1] << 8); txOff += 2; return v; }
    throw new Error('tx varint too large');
  }
  function txReadBytes(n: number): Uint8Array {
    const s = rawTxBytes!.slice(txOff, txOff + n); txOff += n; return s;
  }

  const version = txReadUInt32LE();
  if (rawTxBytes[txOff] === 0x00 && rawTxBytes[txOff + 1] === 0x01) txOff += 2; // skip segwit marker

  const vinCount = txReadVarInt();
  const inputs: Array<{ hash: Uint8Array; index: number; sequence: number }> = [];
  for (let i = 0; i < vinCount; i++) {
    const hash = txReadBytes(32);
    const index = txReadUInt32LE();
    const scriptLen = txReadVarInt(); txReadBytes(scriptLen);
    const sequence = txReadUInt32LE();
    inputs.push({ hash, index, sequence });
  }
  const voutCount = txReadVarInt();
  const outputs: Array<{ value: number; script: Uint8Array }> = [];
  for (let i = 0; i < voutCount; i++) {
    const lo = rawTxBytes[txOff] | (rawTxBytes[txOff+1] << 8) | (rawTxBytes[txOff+2] << 16) | (rawTxBytes[txOff+3] * 0x1000000);
    const hi = rawTxBytes[txOff+4] | (rawTxBytes[txOff+5] << 8) | (rawTxBytes[txOff+6] << 16) | (rawTxBytes[txOff+7] * 0x1000000);
    txOff += 8;
    const value = lo + hi * 0x100000000;
    const scriptLen = txReadVarInt();
    const script = txReadBytes(scriptLen);
    outputs.push({ value, script });
  }
  const locktime = txReadUInt32LE();

  const tx = new bitcoin.Transaction();
  tx.version = version;
  tx.locktime = locktime;
  for (const inp of inputs) tx.addInput(Buffer.from(inp.hash) as Buffer, inp.index, inp.sequence);
  for (const out of outputs) tx.addOutput(Buffer.from(out.script) as Buffer, out.value);

  // Per-input maps — collect tapKeySig (BIP-371 key type 0x13)
  for (let i = 0; i < vinCount; i++) {
    let tapKeySig: Uint8Array | null = null;
    while (offset < psbtBytes.length && psbtBytes[offset] !== 0x00) {
      const kl = readVarInt(); const k = readBytes(kl);
      const vl = readVarInt(); const v = readBytes(vl);
      if (k[0] === 0x13) tapKeySig = v; // PSBT_IN_TAP_KEY_SIG
    }
    offset++;
    if (!tapKeySig) throw new Error(`Signed PSBT input ${i} missing tapKeySig`);
    tx.setWitness(i, [Buffer.from(tapKeySig)]);
  }

  return tx.toHex();
}
